import { DotsVertical, Target01, X } from '@untitledui/icons';
import { useState } from 'react';
import { IconButton } from '../../../components/buttons/IconButton';
import DropDownMenu from '../../../components/general/DropDownMenu';
import MenuItemTile, { type MenuItemTileProps } from '../../../components/general/MenuItemTile';
import { Text } from '../../../components/general/Text';
import { VehicleConnectionIndicator } from '../../../components/general/VehicleConnectionIndicator';
import { VehicleErrorIndicator } from '../../../components/general/VehicleErrorIndicator';
import { VehicleSimulationIndicator } from '../../../components/general/VehicleSimulationIndicator';
import { en } from '../../../locales/en';
import { useGcsStore } from '../../../store/useGcsStore';
import type { VehicleId } from '../../../types/gcs/vehicle';
import { getShortMissionId, getShortVehicleId } from '../../../utils/formatters';
import VehicleStatusTile from '../side-panel/VehicleStatusTile';
interface VehiclePanelHeaderProps {
  gcsId: string;
  vehicleId: VehicleId;
  onFocusVehicle: () => void;
  onClose: () => void;
  menuItems?: MenuItemTileProps[];
}

function VehiclePanelHeader({ gcsId, vehicleId, onFocusVehicle, onClose, menuItems = [] }: VehiclePanelHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const vehicleLabel = getShortVehicleId(vehicleId);

  const missionId = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    const mission = gcsData?.liveMissionsMap.get(vehicleId);
    return mission?.missionId;
  });

  const shortMissionId = missionId ? getShortMissionId(missionId) : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}
    >
      {/* Upper row: vehicle info + action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <Text
          variant='h2'
          style={{
            whiteSpace: 'nowrap',
            flex: '0 0 auto'
          }}
        >
          {vehicleLabel}
        </Text>
        <div
          style={{
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        >
          <VehicleStatusTile gcsId={gcsId} vehicleId={vehicleId} />
        </div>
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <VehicleSimulationIndicator gcsId={gcsId} vehicleId={vehicleId} />
          <VehicleConnectionIndicator gcsId={gcsId} vehicleId={vehicleId} />
          <VehicleErrorIndicator gcsId={gcsId} vehicleId={vehicleId} />
        </div>

        {/* Spacer */}
        <div style={{ flex: '1 1 auto' }} />

        {/* Action buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flex: '0 0 auto'
          }}
        >
          <IconButton icon={Target01} onClick={onFocusVehicle} title={en.vehiclePanelHeader.focusOnVehicle} />

          <div style={{ position: 'relative' }}>
            <IconButton
              icon={DotsVertical}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              title={en.vehiclePanelHeader.moreOptions}
            />

            <DropDownMenu
              isOpen={isMenuOpen && menuItems.length > 0}
              onClose={() => setIsMenuOpen(false)}
              top='100%'
              right='0'
              position='right'
            >
              {menuItems.map((item, index) => (
                <MenuItemTile
                  key={index}
                  {...item}
                  onClick={() => {
                    item.onClick?.();
                    setIsMenuOpen(false);
                  }}
                />
              ))}
            </DropDownMenu>
          </div>

          <IconButton icon={X} onClick={onClose} title={en.vehiclePanelHeader.close} />
        </div>
      </div>

      {/* Mission ID row */}
      <Text
        variant='body2'
        style={{
          color: 'var(--text-secondary)'
        }}
      >
        {shortMissionId ? `${en.vehiclePanelHeader.missionId}: ${shortMissionId}` : ''}
      </Text>
    </div>
  );
}

export default VehiclePanelHeader;
